<template>
  <div class="user-layout">
    <aside class="sidebar">
      <router-link to="/user/new-video" class="brand">
        <img class="brand-mark" src="/favicon.svg" alt="星轨影像工坊">
        <div>
          <div class="brand-name">星轨影像工坊</div>
          <div class="brand-sub">Anime Video Studio</div>
        </div>
      </router-link>

      <nav class="sidebar-nav">
        <router-link v-for="item in menuItems" :key="item.path" :to="item.path" class="nav-item" active-class="--active">
          <span class="nav-icon" v-html="item.icon"></span>
          <span>{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="sidebar-bottom">
        <div class="avatar">{{ username.charAt(0).toUpperCase() }}</div>
        <div class="user-meta">
          <div class="user-name">{{ username }}</div>
          <div class="user-plan">{{ roleLabel }}</div>
        </div>
        <button class="logout" @click="handleLogout">退出</button>
      </div>
    </aside>

    <main class="content-area">
      <div class="mobile-topbar">
        <router-link :to="role === 'admin' ? '/user/admin' : '/user/new-video'" class="mobile-brand">
          <img src="/favicon.svg" alt="星轨影像工坊">
          <span>{{ role === 'admin' ? '管理中心' : '创作台' }}</span>
        </router-link>
        <button @click="handleLogout">退出</button>
      </div>
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const username = ref('用户')
const role = ref('creator')
const roleLabel = computed(() => role.value === 'admin' ? '平台管理员' : '视频创作者')

const iconBase = 'width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"'
const allMenuItems = [
  { path: '/user/admin', label: '管理中心', roles: ['admin'], icon: `<svg ${iconBase}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>` },
  { path: '/user/new-video', label: '创作工坊', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>` },
  { path: '/user/anime-studio', label: '短剧工厂', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><path d="M4 4h16v16H4z"/><path d="M9 4v16M15 4v16M4 9h16M4 15h16"/></svg>` },
  { path: '/user/prompt-library', label: '提示词库', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M4 4v15.5A2.5 2.5 0 0 0 6.5 22H20V4z"/></svg>` },
  { path: '/user/videos', label: '成片剪辑', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>` },
  { path: '/user/publish-center', label: '发布中心', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>` },
  { path: '/user/schedule-center', label: '排期任务', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>` },
  { path: '/user/video-config', label: '创作配置', roles: ['admin'], icon: `<svg ${iconBase}><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 10v6m11-11h-6M7 12H1"/></svg>` },
  { path: '/user/membership', label: '会员中心', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><path d="m3 7 4 10h10l4-10-6 4-3-7-3 7-6-4z"/></svg>` },
  { path: '/user/profile', label: '个人设置', roles: ['admin', 'creator'], icon: `<svg ${iconBase}><circle cx="12" cy="8" r="4"/><path d="M4 22c1.8-4 14.2-4 16 0"/></svg>` }
]
const menuItems = computed(() => allMenuItems.filter(item => item.roles.includes(role.value)))

onMounted(() => {
  username.value = localStorage.getItem('username') || '用户'
  role.value = localStorage.getItem('role') || 'creator'
})

function handleLogout() {
  localStorage.removeItem('token')
  localStorage.removeItem('userId')
  localStorage.removeItem('username')
  localStorage.removeItem('role')
  router.push('/login')
}
</script>

<style scoped>
.user-layout { display: flex; min-height: 100vh; color: var(--text-primary); }
.sidebar {
  width: 252px;
  position: fixed;
  inset: 0 auto 0 0;
  display: flex;
  flex-direction: column;
  padding: 18px 14px;
  background:
    linear-gradient(180deg, rgba(7, 12, 24, .98), rgba(13, 20, 38, .96)),
    linear-gradient(90deg, rgba(54,228,255,.08), transparent);
  border-right: 1px solid rgba(124, 242, 255, .14);
  box-shadow: 14px 0 38px rgba(0, 0, 0, .28);
  z-index: 30;
}
.brand { display: flex; align-items: center; gap: 12px; padding: 8px 10px 18px; text-decoration: none; color: var(--text-primary); }
.brand-mark {
  width: 42px; height: 42px; border-radius: 10px;
  display: block; box-shadow: 0 0 26px rgba(54,228,255,.28);
}
.brand-name { font-weight: 950; font-size: 17px; }
.brand-sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
.sidebar-nav { display: flex; flex-direction: column; gap: 6px; flex: 1; }
.nav-item {
  display: flex; align-items: center; gap: 11px;
  min-height: 42px; padding: 0 13px; border-radius: 8px;
  color: var(--text-secondary); text-decoration: none; font-size: 14px; font-weight: 800;
  border: 1px solid transparent;
}
.nav-item:hover { background: rgba(54,228,255,.07); color: var(--text-primary); }
.nav-item.--active {
  color: #06111f; background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green));
  border-color: rgba(124,242,255,.36);
  box-shadow: 0 12px 28px rgba(54,228,255,.18);
}
.nav-icon { display: flex; color: currentColor; }
.sidebar-bottom {
  display: grid; grid-template-columns: 38px 1fr auto; align-items: center; gap: 10px;
  padding: 12px; border-radius: 10px; background: rgba(255,255,255,.04);
  border: 1px solid rgba(124,242,255,.12);
}
.avatar {
  width: 38px; height: 38px; border-radius: 10px; display: grid; place-items: center;
  background: rgba(141,124,255,.18); color: var(--accent-cyan); font-weight: 900;
}
.user-name { font-size: 13px; font-weight: 900; color: var(--text-primary); }
.user-plan { font-size: 11px; color: var(--text-muted); }
.logout { border: 0; background: transparent; color: var(--accent-green); cursor: pointer; font-weight: 800; }
.content-area {
  margin-left: 252px; flex: 1; min-height: 100vh; padding: 28px;
  background:
    linear-gradient(90deg, rgba(124,242,255,.045) 1px, transparent 1px),
    linear-gradient(0deg, rgba(124,242,255,.04) 1px, transparent 1px),
    radial-gradient(circle at 78% 8%, rgba(141,124,255,.14), transparent 28%),
    linear-gradient(135deg, #080b14 0%, #0d1426 48%, #091a24 100%);
  background-size: 32px 32px, 32px 32px, auto, auto;
}
.mobile-topbar { display: none; }
@media (max-width: 900px) {
  .user-layout { display: block; }
  .sidebar {
    width: auto; height: 68px; inset: auto 0 0 0; padding: 8px 10px;
    border-right: 0; border-top: 1px solid rgba(124,242,255,.18);
    background: rgba(7,12,24,.94); backdrop-filter: blur(18px);
  }
  .brand, .sidebar-bottom { display: none; }
  .sidebar-nav {
    display: grid; grid-auto-flow: column; grid-auto-columns: minmax(64px, 1fr);
    overflow-x: auto; gap: 6px; height: 100%;
  }
  .nav-item {
    min-height: 52px; padding: 5px 7px; border-radius: 10px;
    flex-direction: column; justify-content: center; gap: 3px; font-size: 11px;
  }
  .nav-icon svg { width: 18px; height: 18px; }
  .content-area { margin-left: 0; padding: 14px 12px 86px; min-height: 100vh; }
  .mobile-topbar {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    position: sticky; top: 0; z-index: 20; margin: -14px -12px 14px; padding: 10px 12px;
    background: rgba(7,12,24,.9); border-bottom: 1px solid rgba(124,242,255,.12);
    backdrop-filter: blur(16px);
  }
  .mobile-brand { display: flex; align-items: center; gap: 8px; color: var(--text-primary); text-decoration: none; font-weight: 950; }
  .mobile-brand img { width: 30px; height: 30px; border-radius: 8px; }
  .mobile-topbar button { border: 1px solid rgba(124,242,255,.16); background: rgba(255,255,255,.04); color: var(--accent-green); border-radius: 8px; padding: 6px 10px; font-weight: 900; }
}
</style>
